/**
 * Validation utilities for phone numbers and emails
 */

/**
 * Validate phone number format
 * @param {string} phoneNumber - The phone number to validate
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return { isValid: false, reason: 'Phone number is required' };
  }

  const trimmedPhone = phoneNumber.trim();
  
  if (trimmedPhone.length === 0) {
    return { isValid: false, reason: 'Phone number cannot be empty' };
  }

  // Remove all non-digit characters
  const digitsOnly = trimmedPhone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 digits, or 11 with country code 1)
  if (digitsOnly.length === 10) {
    // Valid 10-digit US number
    return { isValid: true, reason: null };
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    // Valid 11-digit US number with country code
    return { isValid: true, reason: null };
  } else if (digitsOnly.length < 10) {
    return { isValid: false, reason: 'Phone number must be at least 10 digits' };
  } else if (digitsOnly.length > 11) {
    return { isValid: false, reason: 'Phone number is too long' };
  } else {
    return { isValid: false, reason: 'Invalid phone number format' };
  }
}

/**
 * Format phone number for display
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number
 */
export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return phoneNumber;
  
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Take the last 10 digits for US formatting
  const last10 = digitsOnly.slice(-10);
  
  if (last10.length === 10) {
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  }
  
  return phoneNumber; // Return original if can't format
}

/**
 * Format phone number for API submission
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number with +1 prefix
 */
export function formatPhoneNumberForAPI(phoneNumber) {
  if (!phoneNumber) return phoneNumber;
  
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Add +1 country code if not present
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  }
  
  return phoneNumber; // Return original if can't format
}

/**
 * Validate email format
 * @param {string} email - The email to validate
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return { isValid: false, reason: 'Email is required' };
  }

  const trimmedEmail = email.trim();
  
  if (trimmedEmail.length === 0) {
    return { isValid: false, reason: 'Email cannot be empty' };
  }

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, reason: 'Please enter a valid email address' };
  }

  // Check for common invalid patterns
  if (trimmedEmail.includes('..')) {
    return { isValid: false, reason: 'Email cannot contain consecutive dots' };
  }

  if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) {
    return { isValid: false, reason: 'Email cannot start or end with a dot' };
  }

  if (trimmedEmail.includes('@.') || trimmedEmail.includes('.@')) {
    return { isValid: false, reason: 'Invalid email format around @ symbol' };
  }

  // Check length constraints
  if (trimmedEmail.length > 254) {
    return { isValid: false, reason: 'Email address is too long' };
  }

  const [localPart, domain] = trimmedEmail.split('@');
  
  if (localPart.length > 64) {
    return { isValid: false, reason: 'Email local part is too long' };
  }

  if (domain.length > 253) {
    return { isValid: false, reason: 'Email domain is too long' };
  }

  return { isValid: true, reason: null };
}
